import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { observer } from 'mobx-react-lite';

import { Modal } from 'components';
import { NETWORK_SWITCH_DELAY } from 'consts';
import { CT_CHAIN_ID } from 'consts/copyTrading';
import { useBalances, useHandleConnectClick, useIsWrongNetwork, useSwitchChain } from 'hooks';
import { useDeposit } from 'hooks/meme/useDeposit';
import { store } from 'store';
import { bn } from 'utils/BigNumber';
import { addSeparators, cutAddress, getBalance, toDollars, wait } from 'utils/helpers';

import * as S from './CopyTradingModal.styles';

const USD_PRESETS = [10, 100, 250, 500, 1000];

export const DepositModal = observer(() => {
  const { account, balancesMap } = store.web3Store;
  const { portfolio } = store.portfolioStore;
  const {
    value,
    setValue,
    isNoValue,
    tokenIn,
    tokenInAddress,
    inputInDollars,
    walletsToCopy,
    isSubmitting: isDepositing,
    setIsSubmitting,
    isCopyModalOpen,
    copyAction,
    closeCopyModal,
  } = store.copyFormStore;
  const { requestingChainId, pricesMap } = store.commonStore;
  const isSubmitting = isDepositing;
  const isOpen = isCopyModalOpen && copyAction === 'deposit';

  useBalances({
    chainId: CT_CHAIN_ID,
    tokens: [tokenInAddress],
    enabled: isOpen && !isSubmitting,
  });
  const tokenInBalance = getBalance(balancesMap[CT_CHAIN_ID], tokenInAddress);

  const { handleConnectClick } = useHandleConnectClick();
  const { switchChain } = useSwitchChain();
  const isWrongNetwork = useIsWrongNetwork({ expectedChainId: CT_CHAIN_ID });
  const deposit = useDeposit();

  const balanceIn = tokenInBalance || '0';
  const isInsufficientBalance = !!tokenIn && bn(value).toDecimals(tokenIn.decimals).gt(balanceIn);
  const proxyWallet = portfolio?.proxyWallet;

  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setValue(inputValue.replaceAll(' ', ''));
  }, [inputValue, setValue]);

  useEffect(() => {
    setInputValue(addSeparators(value));
  }, [value]);

  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(ev.target.value);
  };

  const handleMaxClick = () => {
    if (!tokenIn) return;
    setInputValue(bn(balanceIn).fromDecimals(tokenIn.decimals).toString());
  };

  const handlePresetClick = (usd: number) => {
    const price = pricesMap[CT_CHAIN_ID]?.[tokenIn.address] || '1';
    const nextValue = bn(usd).div(price).toView();
    setInputValue(nextValue);
  };

  const handleDepositClick = async () => {
    if (!tokenIn || isNoValue || !account) return;

    if (isWrongNetwork) {
      const isChanged = await switchChain(CT_CHAIN_ID);
      if (!isChanged) return;
      await wait(NETWORK_SWITCH_DELAY);
    }
    setIsSubmitting(true);
    await deposit();
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={isSubmitting ? () => {} : closeCopyModal} maxWidth={480}>
      <S.ModalHeader>
        <S.ModalTitle>Deposit {tokenIn.symbol} to Proxy Wallet</S.ModalTitle>
      </S.ModalHeader>

      <S.ModalBody>
        <S.ModalInfo>
          <span>Network</span>
          <span>Polygon</span>
        </S.ModalInfo>

        <S.ModalInfo>
          <span>Proxy Wallet</span>
          {proxyWallet ? (
            <S.AddressLink
              href={`https://polygonscan.com/address/${proxyWallet}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {cutAddress(proxyWallet, 4)}
            </S.AddressLink>
          ) : (
            <span>Will be created on the first deposit</span>
          )}
        </S.ModalInfo>

        <S.ModalInfo>
          <span>Your Balance</span>
          <span>${portfolio?.deposit?.toFixed(2) || '0.00'}</span>
        </S.ModalInfo>

        <S.InputGroup>
          <S.InputLabelRow>
            <S.InputLabel>Deposit Amount</S.InputLabel>
            <S.BalanceRow>
              <span>
                Balance: {bn(balanceIn).toView(tokenIn.decimals)} {tokenIn.symbol}
              </span>
              <S.MaxButton onClick={handleMaxClick}>MAX</S.MaxButton>
            </S.BalanceRow>
          </S.InputLabelRow>
          <S.InputWrapper $hasError={isInsufficientBalance}>
            <NumericFormat
              value={inputValue}
              onChange={handleInputChange}
              inputMode="decimal"
              placeholder="0"
              autoFocus
              thousandSeparator=" "
              allowNegative={false}
              allowedDecimalSeparators={['.', ',']}
              decimalScale={tokenIn.decimals}
              disabled={isSubmitting}
            />
          </S.InputWrapper>
          <S.UsdValue>{!!inputInDollars && toDollars(inputInDollars)}</S.UsdValue>
        </S.InputGroup>

        <S.PresetButtonsRow>
          {USD_PRESETS.map((usd) => (
            <S.PresetButton key={usd} onClick={() => handlePresetClick(usd)}>
              ${usd}
            </S.PresetButton>
          ))}
        </S.PresetButtonsRow>

        <S.WalletsToCopy hidden={walletsToCopy.length === 0}>
          Wallets to copy: {walletsToCopy.length}
        </S.WalletsToCopy>

        {(() => {
          if (!account) {
            return <S.ActionButton onClick={handleConnectClick}>Connect wallet</S.ActionButton>;
          }

          if (requestingChainId) {
            return (
              <S.ActionButton $disabled $isLoading>
                ...Network switching
              </S.ActionButton>
            );
          }

          if (isDepositing) {
            return (
              <S.ActionButton $disabled $isLoading>
                ...Submitting
              </S.ActionButton>
            );
          }

          if (isNoValue) {
            return <S.ActionButton $disabled>Enter amount</S.ActionButton>;
          }

          if (isInsufficientBalance) {
            return <S.ActionButton $disabled>Insufficient balance</S.ActionButton>;
          }

          if (walletsToCopy.length) {
            return <S.ActionButton onClick={handleDepositClick}>Deposit & Copy</S.ActionButton>;
          }

          return <S.ActionButton onClick={handleDepositClick}>Deposit</S.ActionButton>;
        })()}

        <S.ModalHint>
          {tokenIn.symbol} will be transferred on Polygon to the bot's proxy wallet
        </S.ModalHint>
      </S.ModalBody>
    </Modal>
  );
});
